import { useCallback, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { ChevronLeft, PackageCheck, Rocket, Undo2 } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, EnumOption, RowAction } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { useToast } from '@/providers/UiProviders'
import {
  fetchPackVersions,
  rolloutVersion,
  rollbackVersion,
  LOC_STR as S,
  type PackVersionRow,
} from './localizationExtraApi'

/**
 * A22.07 — Версионирование и раскатка. Read-only list of localization pack
 * releases + staged/gradual rollout controls. Dormant until
 * `/admin/localization/versions` ships → fetcher fail-soft to empty.
 * Placeholder permission USERS_VIEW (no LOCALIZATION_* permission defined yet).
 */
export function LocalizationVersions() {
  const { toast } = useToast()

  const statusOptions = useMemo<EnumOption[]>(
    () => [
      { value: 'draft', label: S.statusDraft, status: 'review' },
      { value: 'staged', label: S.statusStaged, status: 'pending' },
      { value: 'rolling', label: S.statusRolling, status: 'pending' },
      { value: 'live', label: S.statusLive, status: 'active' },
      { value: 'rolled_back', label: S.statusRolledBack, status: 'blocked' },
    ],
    [],
  )

  const columns = useMemo<AdminColumn<PackVersionRow>[]>(
    () => [
      { key: 'version', header: S.version, type: 'text' },
      { key: 'lang', header: S.language, type: 'text' },
      { key: 'status', header: S.status, type: 'enum', options: statusOptions },
      { key: 'rollout_pct', header: S.rolloutPct, type: 'number' },
      { key: 'keys', header: S.keys, type: 'number' },
      { key: 'author', header: S.author, type: 'text' },
      { key: 'published_at', header: S.publishedAt, type: 'date' },
    ],
    [statusOptions],
  )

  const doRollout = useCallback(
    async (row: PackVersionRow) => {
      // Gradual promotion: next step towards 100 % (10 → 50 → 100).
      const next = row.rollout_pct < 10 ? 10 : row.rollout_pct < 50 ? 50 : 100
      try {
        await rolloutVersion(row.id, next)
        toast(S.rolledOut, { tone: 'success' })
      } catch {
        toast(S.failed, { tone: 'error' })
      }
    },
    [toast],
  )

  const doRollback = useCallback(
    async (row: PackVersionRow) => {
      try {
        await rollbackVersion(row.id)
        toast(S.rolledBack, { tone: 'success' })
      } catch {
        toast(S.failed, { tone: 'error' })
      }
    },
    [toast],
  )

  const rowActions = useMemo<RowAction<PackVersionRow>[]>(
    () => [
      {
        id: 'rollout',
        label: S.rollout,
        icon: <Rocket size={14} />,
        onSelect: doRollout,
        visible: (r) => r.status !== 'rolled_back' && r.rollout_pct < 100,
      },
      {
        id: 'rollback',
        label: S.rollback,
        icon: <Undo2 size={14} />,
        danger: true,
        onSelect: doRollback,
        visible: (r) => r.status === 'live' || r.status === 'rolling',
      },
    ],
    [doRollout, doRollback],
  )

  const fetcher = useCallback(fetchPackVersions, [])
  return (
    <>
      <PageHeader
        title={S.versionsTitle}
        screenId="A22.07"
        actions={
          <Link
            to="/localization"
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {S.back}
          </Link>
        }
      />
      <DataTable<PackVersionRow>
        screenId="A22.07"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        rowActions={rowActions}
        exportable
        emptyState={{ icon: <PackageCheck size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}
